import { AUDIT_MY_RECOMMENDATIONS } from '../../utils/ApiUrls';

const myRecommendationService = {
  // === MY RECOMMENDATIONS ===
  
  /**
   * Get list of recommendations owned by current user
   */
  getMyRecommendations: async (token, filters = {}, pagination = {}) => {
    try {
      const params = new URLSearchParams();
      
      // Add filters
      if (filters.search) params.append('search', filters.search);
      if (filters.status) params.append('status', filters.status);
      if (filters.ordering) params.append('ordering', filters.ordering);
      
      // Add pagination
      if (pagination.page) params.append('page', pagination.page);
      if (pagination.page_size) params.append('page_size', pagination.page_size);

      const response = await fetch(`${AUDIT_MY_RECOMMENDATIONS}?${params}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (response.ok) {
        return { success: true, data };
      } else {
        return { success: false, error: data.detail || 'Error fetching my recommendations' };
      }
    } catch (error) {
      console.error('Error in getMyRecommendations:', error);
      return { success: false, error: 'Network error occurred' };
    }
  },

  /**
   * Get detailed information about a specific recommendation owned by current user
   */
  getMyRecommendationDetail: async (token, recommendationId) => {
    try {
      const response = await fetch(`${AUDIT_MY_RECOMMENDATIONS}${recommendationId}/`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (response.ok) {
        return { success: true, data };
      } else {
        return { success: false, error: data.detail || 'Error fetching recommendation details' };
      }
    } catch (error) {
      console.error('Error in getMyRecommendationDetail:', error);
      return { success: false, error: 'Network error occurred' };
    }
  },

  /**
   * Start a recommendation owned by current user
   */
  startMyRecommendation: async (token, recommendationId) => {
    try {
      const response = await fetch(`${AUDIT_MY_RECOMMENDATIONS}${recommendationId}/start/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (response.ok) {
        return { success: true, data };
      } else {
        return { success: false, error: data.detail || 'Error starting recommendation' };
      }
    } catch (error) {
      console.error('Error in startMyRecommendation:', error);
      return { success: false, error: 'Network error occurred' };
    }
  },

  /**
   * Complete a recommendation owned by current user
   */
  completeMyRecommendation: async (token, recommendationId) => {
    try {
      const response = await fetch(`${AUDIT_MY_RECOMMENDATIONS}${recommendationId}/complete/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (response.ok) {
        return { success: true, data };
      } else {
        return { success: false, error: data.detail || 'Error completing recommendation' };
      }
    } catch (error) {
      console.error('Error in completeMyRecommendation:', error);
      return { success: false, error: 'Network error occurred' };
    }
  },

  /**
   * Achieve a recommendation owned by current user
   */
  achieveMyRecommendation: async (token, recommendationId) => {
    try {
      const response = await fetch(`${AUDIT_MY_RECOMMENDATIONS}${recommendationId}/achieve/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (response.ok) {
        return { success: true, data };
      } else {
        return { success: false, error: data.detail || 'Error achieving recommendation' };
      }
    } catch (error) {
      console.error('Error in achieveMyRecommendation:', error);
      return { success: false, error: 'Network error occurred' };
    }
  },

  // === FOLLOW-UPS ===

  /**
   * Get list of follow-ups for a recommendation
   */
  getRecommendationFollowUps: async (token, recommendationId, filters = {}, pagination = {}) => {
    try {
      const params = new URLSearchParams();
      
      // Add filters
      if (filters.search) params.append('search', filters.search);
      if (filters.status) params.append('status', filters.status);
      if (filters.ordering) params.append('ordering', filters.ordering);
      
      // Add pagination
      if (pagination.page) params.append('page', pagination.page);
      if (pagination.page_size) params.append('page_size', pagination.page_size);

      const response = await fetch(`${AUDIT_MY_RECOMMENDATIONS}${recommendationId}/followups/?${params}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (response.ok) {
        return { success: true, data };
      } else {
        return { success: false, error: data.detail || 'Error fetching follow-ups' };
      }
    } catch (error) {
      console.error('Error in getRecommendationFollowUps:', error);
      return { success: false, error: 'Network error occurred' };
    }
  },

  /**
   * Create a new follow-up for a recommendation
   */
  createFollowUp: async (token, recommendationId, followUpData) => {
    try {
      const response = await fetch(`${AUDIT_MY_RECOMMENDATIONS}${recommendationId}/followups/create/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(followUpData),
      });

      const data = await response.json();

      if (response.ok) {
        return { success: true, data };
      } else {
        return { success: false, error: data.detail || 'Error creating follow-up', errors: data };
      }
    } catch (error) {
      console.error('Error in createFollowUp:', error);
      return { success: false, error: 'Network error occurred' };
    }
  },

  /**
   * Get detailed information about a specific follow-up
   */
  getFollowUpDetail: async (token, recommendationId, followUpId) => {
    try {
      const response = await fetch(`${AUDIT_MY_RECOMMENDATIONS}${recommendationId}/followups/${followUpId}/`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (response.ok) {
        return { success: true, data };
      } else {
        return { success: false, error: data.detail || 'Error fetching follow-up details' };
      }
    } catch (error) {
      console.error('Error in getFollowUpDetail:', error);
      return { success: false, error: 'Network error occurred' };
    }
  },

  /**
   * Update a follow-up
   */
  updateFollowUp: async (token, recommendationId, followUpId, followUpData) => {
    try {
      const response = await fetch(`${AUDIT_MY_RECOMMENDATIONS}${recommendationId}/followups/${followUpId}/update/`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(followUpData),
      });

      const data = await response.json();

      if (response.ok) {
        return { success: true, data };
      } else {
        return { success: false, error: data.detail || 'Error updating follow-up', errors: data };
      }
    } catch (error) {
      console.error('Error in updateFollowUp:', error);
      return { success: false, error: 'Network error occurred' };
    }
  },

  /**
   * Delete a follow-up
   */
  deleteFollowUp: async (token, recommendationId, followUpId) => {
    try {
      const response = await fetch(`${AUDIT_MY_RECOMMENDATIONS}${recommendationId}/followups/${followUpId}/delete/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        return { success: true, data };
      } else {
        const data = await response.json();
        return { success: false, error: data.detail || 'Error deleting follow-up' };
      }
    } catch (error) {
      console.error('Error in deleteFollowUp:', error);
      return { success: false, error: 'Network error occurred' };
    }
  },

  // === ATTACHMENTS ===

  /**
   * Upload an attachment for a follow-up
   */
  uploadFollowUpAttachment: async (token, recommendationId, followUpId, file, fileName) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('file_name', fileName || file.name);

      const response = await fetch(`${AUDIT_MY_RECOMMENDATIONS}${recommendationId}/followups/${followUpId}/attachments/upload/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (response.ok) {
        return { success: true, data };
      } else {
        return { success: false, error: data.detail || 'Error uploading attachment', errors: data };
      }
    } catch (error) {
      console.error('Error in uploadFollowUpAttachment:', error);
      return { success: false, error: 'Network error occurred' };
    }
  },

  /**
   * Delete a follow-up attachment
   */
  deleteFollowUpAttachment: async (token, recommendationId, followUpId, attachmentId) => {
    try {
      const response = await fetch(`${AUDIT_MY_RECOMMENDATIONS}${recommendationId}/followups/${followUpId}/attachments/${attachmentId}/delete/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        return { success: true, data };
      } else {
        const data = await response.json();
        return { success: false, error: data.detail || 'Error deleting attachment' };
      }
    } catch (error) {
      console.error('Error in deleteFollowUpAttachment:', error);
      return { success: false, error: 'Network error occurred' };
    }
  },

  /**
   * Format follow-up data for API submission
   */
  formatFollowUpData: (formData) => {
    return {
      progression_percentage: parseInt(formData.progression_percentage) || 0,
      recommendation_status_selected: formData.recommendation_status_selected || 'in_progress',
      notes: formData.notes || '',
    };
  },
};

export default myRecommendationService;